import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';

const POLL_MS = 30000;

const FALLBACK_RELATED = [
  { label: 'API Latency', path: '/admin/api-latency' },
  { label: 'API Response Stats', path: '/admin/api-response-stats' },
];

function getHeaders() {
  const t = sessionStorage.getItem('sessionToken');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

function overallColor(overall) {
  if (overall === 'healthy') return 'success';
  if (overall === 'degraded') return 'warning';
  if (overall === 'down') return 'error';
  return 'default';
}

function ProbeRow({ title, probe }) {
  if (!probe) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {probe.url}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={probe.ok ? 'OK' : 'FAIL'}
          color={probe.ok ? 'success' : 'error'}
          variant={probe.ok ? 'outlined' : 'filled'}
        />
        <Typography variant="body2">
          {probe.statusCode != null ? `HTTP ${probe.statusCode}` : 'no status'} ·{' '}
          {probe.latencyMs != null ? `${probe.latencyMs} ms` : '—'}
        </Typography>
      </Box>
      {probe.error ? (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {probe.error}
        </Typography>
      ) : null}
    </Box>
  );
}

function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/admin/system-health/summary`, {
        credentials: 'include',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.message || `Failed (${res.status})`);
        return;
      }
      if (json.statusCode != null && json.statusCode !== '10000') {
        setError(json.message || `Failed (${json.statusCode})`);
        return;
      }
      setData(json.data || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  const services = data?.services || [];
  const relatedAdmin =
    Array.isArray(data?.notes?.relatedAdmin) && data.notes.relatedAdmin.length > 0
      ? data.notes.relatedAdmin
      : FALLBACK_RELATED;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-8">
                <h3>System Health</h3>
                <Typography variant="body2" color="text.secondary">
                  Live and deep probes for dugout-be, chat, and feeds. Auto-refreshes every 30s.
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={fetchSummary} disabled={loading}>
                  Refresh
                </Button>
                {loading ? <CircularProgress size={22} /> : null}
                {data?.checkedAt ? (
                  <Typography variant="body2" color="text.secondary">
                    Checked at {new Date(data.checkedAt).toLocaleString()}
                  </Typography>
                ) : null}
              </Box>

              {error ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              ) : null}

              <Alert severity="info" sx={{ mb: 2 }}>
                {data?.notes?.cloudwatch ||
                  'ECS desired vs running, restarts, deploy/rollback, and ALB RPS/latency/5xx come from AWS CloudWatch / ECS — not from these health URLs.'}
                <Box component="span" sx={{ display: 'block', mt: 1 }}>
                  Related:{' '}
                  {relatedAdmin.map((r, i) => (
                    <React.Fragment key={r.path}>
                      {i > 0 ? ' · ' : null}
                      <Link to={r.path}>{r.label}</Link>
                    </React.Fragment>
                  ))}
                </Box>
              </Alert>

              {!loading && !error && services.length === 0 ? (
                <Alert severity="warning">No services returned from the health summary.</Alert>
              ) : null}

              <Grid container spacing={2}>
                {services.map((svc) => (
                  <Grid item xs={12} md={4} key={svc.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="h6">{svc.label || svc.id}</Typography>
                          <Chip
                            label={svc.overall || 'unknown'}
                            color={overallColor(svc.overall)}
                            size="small"
                            sx={{ textTransform: 'uppercase' }}
                          />
                        </Box>
                        <ProbeRow title="Live" probe={svc.live} />
                        <ProbeRow title="Deep" probe={svc.deep} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default SystemHealth;
